// First-party analytics — one lightweight ping to the backend per route
// change, funnel events (checkout started), and the session's attribution
// (landing page, referrer, UTMs) so a purchase can be tied back to the page
// and campaign that produced it. Never tracks /admin, never tracks headless
// browsers (prerender pass, bots that admit it), and never throws.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const UTM_PARAMS = {
  utm_source: "utmSource",
  utm_medium: "utmMedium",
  utm_campaign: "utmCampaign",
  utm_term: "utmTerm",
  utm_content: "utmContent",
};

const newId = () =>
  window.crypto?.randomUUID
    ? window.crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

// Session id: lives for the tab (sessionStorage) — one "visit"
const getSessionId = () => {
  try {
    let id = sessionStorage.getItem("ms:sid");
    if (!id) {
      id = newId();
      sessionStorage.setItem("ms:sid", id);
    }
    return id;
  } catch {
    return "";
  }
};

// Visitor id: persists across visits (localStorage) — one anonymous
// browser/device. A session is "returning" if the visitor id already
// existed when the session began.
const getVisitor = () => {
  try {
    let id = localStorage.getItem("ms:vid");
    let created = false;
    if (!id) {
      id = newId();
      localStorage.setItem("ms:vid", id);
      created = true;
    }
    let returning = sessionStorage.getItem("ms:returning");
    if (returning === null) {
      returning = created ? "0" : "1";
      sessionStorage.setItem("ms:returning", returning);
    }
    return { visitorId: id, returning: returning === "1" };
  } catch {
    return { visitorId: "", returning: false };
  }
};

// Attribution is captured once, on the first page of the session, and then
// reused for every ping and for checkout.
const getAttributionState = () => {
  try {
    const raw = sessionStorage.getItem("ms:attr");
    if (raw) return JSON.parse(raw);
    const params = new URLSearchParams(window.location.search);
    const attr = { landingPath: window.location.pathname.slice(0, 300), referrer: "" };
    try { attr.referrer = document.referrer ? new URL(document.referrer).host : ""; } catch { attr.referrer = ""; }
    Object.entries(UTM_PARAMS).forEach(([param, key]) => {
      const v = params.get(param);
      attr[key] = v ? v.slice(0, 120) : "";
    });
    sessionStorage.setItem("ms:attr", JSON.stringify(attr));
    return attr;
  } catch {
    return {};
  }
};

const post = (url, body) => {
  fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    keepalive: true,
  }).catch(() => {});
};

// The browser's persistent anonymous id — also the key for "My Documents"
export function getVisitorId() {
  return getVisitor().visitorId;
}

// Everything the checkout stamps into the payment so the purchase links
// back to its session, landing page, and campaign.
export function getAttribution() {
  try {
    const { visitorId } = getVisitor();
    return { sessionId: getSessionId(), visitorId, ...getAttributionState() };
  } catch {
    return {};
  }
}

export function trackPageView(path) {
  try {
    if (!path || path.startsWith("/admin")) return;
    if (navigator.webdriver) return; // headless prerender / honest bots
    const sessionId = getSessionId();
    if (!sessionId) return;
    const { visitorId, returning } = getVisitor();
    const attr = getAttributionState();
    post(`${BACKEND_URL}/api/track`, {
      path,
      sessionId,
      visitorId,
      returning,
      referrer: document.referrer || "",
      landingPath: attr.landingPath || path,
      utm: {
        utmSource: attr.utmSource || "",
        utmMedium: attr.utmMedium || "",
        utmCampaign: attr.utmCampaign || "",
        utmTerm: attr.utmTerm || "",
        utmContent: attr.utmContent || "",
      },
    });
  } catch {
    /* analytics must never break the page */
  }
}

// Funnel events — currently "checkout_start" from the payment sheet
export function trackEvent(event, { documentType = "", code = "" } = {}) {
  try {
    if (!event || navigator.webdriver) return;
    const sessionId = getSessionId();
    if (!sessionId) return;
    const { visitorId } = getVisitor();
    post(`${BACKEND_URL}/api/track-event`, { sessionId, visitorId, event, documentType: documentType || "", code: code || "" });
  } catch {
    /* never break the page */
  }
}
